import { useState } from "react";
import { Button, Form, Input, Popup, Select } from "semantic-ui-react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import "react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css";
import moment from "moment";

const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  key: i,
  text: String(i).padStart(2, "0"),
  value: i,
}));

const minuteOptions = Array.from({ length: 12 }, (_, i) => ({
  key: i * 5,
  text: String(i * 5).padStart(2, "0"),
  value: i * 5,
}));

const todayAtMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

interface Props {
  label: string;
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
  includeTime?: boolean;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const DateTimePicker = ({
  label,
  value,
  onChange,
  includeTime,
  disabled,
  error,
  required,
  minDate,
  maxDate,
}: Props) => {
  const [dateOpen, setDateOpen] = useState(false);
  const [localHour, setLocalHour] = useState(0);
  const [localMinute, setLocalMinute] = useState(0);

  const currentHour = value ? value.getHours() : localHour;
  const currentMinute = value ? value.getMinutes() : localMinute;

  const displayValue = value
    ? includeTime
      ? `${moment(value).format("L")} ${String(currentHour).padStart(2, "0")}:${String(currentMinute).padStart(2, "0")}`
      : moment(value).format("L")
    : "";

  const handleDateChange = (_: any, data: any) => {
    const newDate = data.value as Date | null;
    if (!newDate) return;
    const updated = new Date(newDate);
    updated.setHours(includeTime ? currentHour : 0, includeTime ? currentMinute : 0, 0, 0);
    onChange(updated);
    setDateOpen(false);
  };

  const handleHourChange = (hour: number) => {
    setLocalHour(hour);
    const base = value ? new Date(value) : todayAtMidnight();
    base.setHours(hour, value ? currentMinute : localMinute, 0, 0);
    onChange(base);
  };

  const handleMinuteChange = (minute: number) => {
    setLocalMinute(minute);
    const base = value ? new Date(value) : todayAtMidnight();
    base.setHours(value ? currentHour : localHour, minute, 0, 0);
    onChange(base);
  };

  const handleClear = () => {
    setLocalHour(0);
    setLocalMinute(0);
    onChange(undefined);
  };

  return (
    <Form.Field required={required} error={!!error}>
      <label>{label}</label>
      <Input action>
        <Input
          error={!!error}
          value={displayValue}
          readOnly
          disabled={disabled}
          placeholder={includeTime ? "Select date and time" : "Select date"}
        />
        <Popup
          basic
          open={dateOpen}
          onClose={() => setDateOpen(false)}
          on="click"
          position="bottom right"
          style={{ border: 0, margin: 0, padding: 0, boxShadow: "none" }}
          trigger={
            <Button
              color={error ? "red" : undefined}
              icon="calendar"
              type="button"
              disabled={disabled}
              onClick={() => setDateOpen(true)}
            />
          }
        >
          <SemanticDatepicker
            inline
            value={value}
            locale="en-US"
            onChange={handleDateChange}
            type="basic"
            minDate={minDate}
            maxDate={maxDate}
          />
        </Popup>
        {includeTime && (
          <Popup
            basic
            on="click"
            position="bottom right"
            style={{ margin: 2, boxShadow: "none" }}
            trigger={<Button color={error ? "red" : undefined} icon="clock" type="button" disabled={disabled} />}
          >
            <Form>
              <Form.Group>
                <Form.Field
                  control={Select}
                  label="Hour"
                  options={hourOptions}
                  value={currentHour}
                  onChange={(_: any, { value: v }: any) => handleHourChange(v)}
                  compact
                />
                <Form.Field
                  control={Select}
                  label="Minute"
                  options={minuteOptions}
                  value={currentMinute}
                  onChange={(_: any, { value: v }: any) => handleMinuteChange(v)}
                  compact
                />
              </Form.Group>
            </Form>
          </Popup>
        )}
        {value && <Button icon="times" type="button" disabled={disabled} onClick={handleClear} />}
      </Input>
      {error && <div className="ui pointing above prompt label">{error}</div>}
    </Form.Field>
  );
};

export default DateTimePicker;
